import React, { useState } from 'react';
import { Button, Modal } from '@vacasa/react-components-lib';
import { DetailedReview, ReviewContentItem, ReviewContentStatus, Roles } from '@reviews/interfaces';
import { FrontendUtils } from '@reviews/frontend';
import { useDeleteReviewContentMutation } from '../../services';
import { useSelector } from 'react-redux';
import { Store } from '../../store';
import { Spinner } from '../../assests/icons/icons';
import { useLoading } from '../../contexts/LoadingContext';
import * as _ from 'lodash';
import './ReviewContentSection.scss';

interface ReviewContentSectionProps {
    review: DetailedReview;
}

const statusLabel: Record<ReviewContentStatus, string> = {
    [ReviewContentStatus.PENDING]: 'Pending',
    [ReviewContentStatus.APPROVED]: 'Approved',
    [ReviewContentStatus.COMPLAINT]: 'Complaint',
    [ReviewContentStatus.REJECTED]: 'Rejected',
};

export const ReviewContentSection: React.FC<ReviewContentSectionProps> = ({ review }) => {
    const contents = (review.review_contents ?? []).slice().sort((a, b) => a.sort_order - b.sort_order);
    const flow = useSelector((state: Store) => state.flow);
    const isAdmin = flow.user?.role === Roles.ADMIN;
    const { setLoadingConfig } = useLoading();
    const [deleteReviewContent] = useDeleteReviewContentMutation();
    const [contentToDelete, setContentToDelete] = useState<ReviewContentItem | null>(null);
    const [contentToReveal, setContentToReveal] = useState<ReviewContentItem | null>(null);
    const [revealedContentIds, setRevealedContentIds] = useState<number[]>([]);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState(false);

    if (!isAdmin || _.isEmpty(contents)) {
        return null;
    }

    const isRejectedBlurred = (content: ReviewContentItem) =>
        content.ai_validation_status === ReviewContentStatus.REJECTED && !revealedContentIds.includes(content.review_content_id);

    const handleConfirmDelete = async () => {
        if (!contentToDelete) {
            return;
        }

        setIsDeleting(true);
        setDeleteError(false);
        setLoadingConfig(true, false);
        try {
            await deleteReviewContent({
                reviewId: _.toNumber(review.review_id),
                contentId: _.toNumber(contentToDelete.review_content_id),
            }).unwrap();
            setContentToDelete(null);
        } catch {
            setDeleteError(true);
        } finally {
            setIsDeleting(false);
            setLoadingConfig(false);
        }
    };

    const handleConfirmReveal = () => {
        if (!contentToReveal) {
            return;
        }

        setRevealedContentIds((prev) => _.uniq([...prev, contentToReveal.review_content_id]));
        setContentToReveal(null);
    };

    const renderImage = (content: ReviewContentItem) => {
        const thumbnailUrl = FrontendUtils.buildGuestContentDisplayUrl(content.read_url, 'thumbnail');
        const previewUrl = FrontendUtils.buildGuestContentDisplayUrl(content.read_url, 'preview');

        if (!thumbnailUrl || !previewUrl) {
            return <div className="review-content-card__placeholder">No preview</div>;
        }

        const blurred = isRejectedBlurred(content);
        const image = (
            <img
                src={thumbnailUrl}
                alt={content.description || `Guest photo ${content.sort_order + 1}`}
                className={blurred ? 'review-content-card__image--blurred' : undefined}
            />
        );

        if (blurred) {
            return (
                <div className="review-content-card__image-wrap is-blurred">
                    <div className="review-content-card__image-inner" aria-hidden>
                        {image}
                    </div>
                    <div className="review-content-card__blur-overlay">
                        <Button customClass="review-content-card__show" variant="secondary" onClick={() => setContentToReveal(content)}>
                            Show
                        </Button>
                    </div>
                </div>
            );
        }

        return (
            <div className="review-content-card__image-wrap">
                <a href={previewUrl} target="_blank" rel="noreferrer">
                    {image}
                </a>
            </div>
        );
    };

    const renderDeleteModalBody = () => {
        if (isDeleting) {
            return (
                <div className="processing-spinner">
                    <Spinner />
                </div>
            );
        }

        return (
            <React.Fragment>
                {deleteError ? (
                    <p className="review-content-section__error">An error occurred while deleting this image. Please try again.</p>
                ) : (
                    <p className="body-regular">
                        This will permanently remove the image from the review and from storage. This cannot be undone.
                    </p>
                )}
                <div className="review-content-section__actions">
                    <Button customClass="cancel-btn" variant="info" onClick={() => setContentToDelete(null)}>
                        Cancel
                    </Button>
                    {!deleteError && (
                        <Button variant="secondary" onClick={handleConfirmDelete}>
                            Delete image
                        </Button>
                    )}
                </div>
            </React.Fragment>
        );
    };

    return (
        <div className="review-content-section">
            <h5 className="heading-card-24 review-content-section__title">Review Content</h5>
            <div className="review-content-section__grid">
                {contents.map((content) => (
                    <div key={content.review_content_id} className="review-content-card">
                        {renderImage(content)}
                        <div className="review-content-card__meta">
                            <span className={`review-content-card__status status-${content.ai_validation_status.toLowerCase()}`}>
                                {statusLabel[content.ai_validation_status] ?? content.ai_validation_status}
                            </span>
                            {content.description && <p className="body-regular review-content-card__description">{content.description}</p>}
                            {content.complaint_reason && (
                                <p className="body-regular review-content-card__complaint">{content.complaint_reason}</p>
                            )}
                            {content.rejection_reason && (
                                <p className="body-regular review-content-card__rejection">{content.rejection_reason}</p>
                            )}
                            <Button
                                customClass="review-content-card__delete"
                                variant="info"
                                onClick={() => {
                                    setDeleteError(false);
                                    setContentToDelete(content);
                                }}
                            >
                                Delete
                            </Button>
                        </div>
                    </div>
                ))}
            </div>

            {contentToReveal && (
                <Modal showModal={!!contentToReveal} setShowModal={() => setContentToReveal(null)} size="medium">
                    <div>
                        <span className="body-small-bold">Sensitive content warning</span>
                    </div>
                    <div>
                        <p className="body-regular">This image may contain inappropriate or violent content. Do you want to show it?</p>
                        <div className="review-content-section__actions">
                            <Button customClass="cancel-btn" variant="info" onClick={() => setContentToReveal(null)}>
                                Cancel
                            </Button>
                            <Button variant="secondary" onClick={handleConfirmReveal}>
                                Show image
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}

            {contentToDelete && (
                <Modal
                    showModal={!!contentToDelete}
                    setShowModal={() => {
                        if (!isDeleting) {
                            setContentToDelete(null);
                        }
                    }}
                    size="medium"
                >
                    <div>
                        <span className="body-small-bold">Delete review content</span>
                    </div>
                    <div>{renderDeleteModalBody()}</div>
                </Modal>
            )}
        </div>
    );
};
